import React, { useState } from 'react';
import './deviceSettingsSurfaces.css';

export function ThresholdInputs({ value, onChangeMin, onChangeMax, t }) {
  return (
    <>
      <div className="col-6 col-sm-4">
        <input
          type="number"
          className="form-control"
          placeholder={t('args.min')}
          value={value?.min ?? ''}
          onChange={(event) => onChangeMin(event.target.value)}
        />
      </div>
      <div className="col-6 col-sm-4">
        <input
          type="number"
          className="form-control"
          placeholder={t('args.max')}
          value={value?.max ?? ''}
          onChange={(event) => onChangeMax(event.target.value)}
        />
      </div>
    </>
  );
}

function normalizeRelayValue(value) {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return numeric === 1;
}

export function RelayModeControl({
  relayKey,
  label,
  value,
  mode = 'auto',
  pending,
  onRelayCommand,
  t,
  disabled = false,
  stale = false,
}) {
  const isOn = normalizeRelayValue(value);
  const isAuto = mode === 'auto';
  const isManualOn = mode === 'manual_on';
  const isManualOff = mode === 'manual_off';
  const modeBadge = isAuto
    ? t('deviceSettingsModal.modeAutoBadge')
    : isManualOn
    ? t('deviceSettingsModal.modeManualOnBadge')
    : t('deviceSettingsModal.modeManualOffBadge');
  return (
    <div
      className={`device-settings-modal__relay-status ${isOn ? 'is-on' : 'is-off'} ${pending ? 'is-pending' : ''} ${stale ? 'is-stale' : ''}`}
    >
      <span className={`device-settings-modal__relay-dot ${isOn ? 'is-on' : 'is-off'}`} />
      <span className="device-settings-modal__relay-label">
        <span>{label}</span>
        <small className="device-settings-modal__relay-state">
          {isOn ? t('deviceSettingsModal.outletOn') : t('deviceSettingsModal.outletOff')}
        </small>
      </span>
      <div className="device-settings-modal__relay-actions">
        <small className="text-muted">{modeBadge}</small>
        <div className="btn-group btn-group-sm" role="group" aria-label={t('deviceSettingsModal.relayModeAria', label)}>
          <button
            type="button"
            className={`btn ${isAuto ? 'btn-success' : 'btn-outline-secondary'} ${pending ? 'opacity-75' : ''}`}
            onClick={() => onRelayCommand?.(relayKey, 'auto')}
            disabled={pending || disabled || !onRelayCommand}
          >
            {t('deviceSettingsModal.modeAuto')}
          </button>
          <button
            type="button"
            className={`btn ${isManualOn ? 'btn-success' : 'btn-outline-secondary'} ${pending ? 'opacity-75' : ''}`}
            onClick={() => onRelayCommand?.(relayKey, 'manual_on')}
            disabled={pending || disabled || !onRelayCommand}
          >
            {t('deviceSettingsModal.modeManualOn')}
          </button>
          <button
            type="button"
            className={`btn ${isManualOff ? 'btn-danger' : 'btn-outline-secondary'} ${pending ? 'opacity-75' : ''}`}
            onClick={() => onRelayCommand?.(relayKey, 'manual_off')}
            disabled={pending || disabled || !onRelayCommand}
          >
            {t('deviceSettingsModal.modeManualOff')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Shared settings body (thresholds, relays, CO₂, alerts) — used by DeviceSettingsModal and DeviceSettingsPage.
 */
export default function DeviceSettingsCore({
  onSaveThresholds,
  canSaveThresholds,
  saving,
  controller,
  t,
  telegramEnabled,
  setTelegramEnabled,
  powerAlerts,
  setPowerAlerts,
  thresholdAlerts,
  setThresholdAlerts,
  trendAlerts,
  setTrendAlerts,
  thresholds,
  setThresholdField,
  waterConfig,
  setWaterConfigField,
  timeSyncPending,
  co2OffsetDraft,
  setCo2OffsetDraft,
  calibrationTargetPpm,
  setCalibrationTargetPpm,
  onForceCalibrate,
  pendingCalibration = false,
  calibrationStartedUi = false,
  calibrationCountdownText = '05:00',
  settingsStatus,
  settingsProgressText,
  deviceName,
  setDeviceName,
  relayStatuses = {},
  relayModes = {},
  onRelayCommand,
  relayPending = {},
  relayCommandStatus = null,
  onSaveDisplayName = null,
  nameSavePending = false,
  relayControlsDisabled = false,
  relayStale = false,
  canRestartController = false,
  onRestartController = null,
  restartPending = false,
  restartStatus = null,
  notificationDelivery = null,
  notificationActionPending = false,
  onEnableClimateAlerts = null,
}) {
  const [showCalibrateConfirm, setShowCalibrateConfirm] = useState(false);
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);
  const thresholdSaveHandler = onSaveThresholds;
  const canSaveThresholdsEffective = typeof canSaveThresholds === 'boolean' ? canSaveThresholds : true;
  const showClimateNotificationDelivery = notificationDelivery?.applies === true;
  const climateDeliveryEffective = notificationDelivery?.effectiveClimateAlertsEnabled === true;
  const controllerClimateAlertsEnabled =
    notificationDelivery?.controllerAlertsEnabled ?? telegramEnabled;
  const climateDeliveryWarning = (() => {
    if (!showClimateNotificationDelivery || !controllerClimateAlertsEnabled) return null;
    if (notificationDelivery?.warningCode === 'no_eligible_recipients') {
      return t('deviceSettingsModal.climateDeliveryNoRecipients');
    }
    if (notificationDelivery?.warningCode === 'climate_category_disabled') {
      return t('deviceSettingsModal.climateDeliveryClimateDisabled');
    }
    return null;
  })();
  const climateDeliveryTitle = climateDeliveryEffective
    ? t('deviceSettingsModal.climateDeliveryOn')
    : controllerClimateAlertsEnabled
      ? t('deviceSettingsModal.climateDeliveryBlocked')
      : t('deviceSettingsModal.climateDeliveryOff');
  const climateDeliveryTone = climateDeliveryEffective
    ? 'success'
    : controllerClimateAlertsEnabled
      ? 'warning'
      : 'secondary';
  const canEnableClimateFromSite =
    showClimateNotificationDelivery &&
    notificationDelivery?.currentUser?.canEnableClimateFromSite === true &&
    typeof onEnableClimateAlerts === 'function';

  const climateRows = [
    { section: 'day', title: t('day'), items: [['temperature', t('temperature')], ['humidity', t('humidity')], ['co2', t('co2')]] },
    { section: 'night', title: t('night'), items: [['temperature', t('temperature')], ['humidity', t('humidity')], ['co2', t('co2')]] },
  ];

  const waterRows = [
    ['ph', 'pH'],
    ['ec', 'EC'],
    ['orp', 'ORP'],
    ['temp', t('waterTemperature') || 'Water temperature'],
    ['level', t('waterLevel') || 'Water level'],
  ];
  const waterConfigRows = [
    ['tankMaxHeight', t('maxHeight') || 'Max height', 'cm'],
    ['tankFullVolume', t('waterVolume') || 'Tank volume', 'L'],
    ['dMin', t('displayInterval') || 'Display interval', 'min'],
    ['sMin', t('serverInterval') || 'Server interval', 'min'],
  ];

  const renderStatus = (status) => {
    if (!status) return null;
    const type = status.type === 'success' ? 'success' : status.type === 'warning' ? 'warning' : 'danger';
    return (
      <div className={`device-settings-modal__inline-status alert alert-${type} py-2 px-3 mb-0`}>
        {status.text}
      </div>
    );
  };

  return (
    <div className="device-settings-modal__body">
      <div className="device-settings-modal__section">
        <div className="device-settings-modal__section-head">
          <div>
            <h6 className="fw-bold mb-1">{t('deviceSettingsModal.identityTitle')}</h6>
            <p className="small text-muted mb-0">{t('deviceSettingsModal.identityHint')}</p>
          </div>
        </div>
        <input
          type="text"
          className="form-control"
          value={deviceName ?? ''}
          onChange={(event) => setDeviceName?.(event.target.value)}
          placeholder={t('deviceSettingsModal.deviceNamePlaceholder')}
        />
        {onSaveDisplayName ? (
          <button
            type="button"
            className="btn btn-sm btn-primary mt-2"
            style={{ borderRadius: 12 }}
            disabled={nameSavePending}
            onClick={onSaveDisplayName}
          >
            {nameSavePending ? `${t('save')}…` : t('save')}
          </button>
        ) : null}
      </div>

      <div className="device-settings-modal__section">
        <div className="device-settings-modal__section-head">
          <div>
            <h6 className="fw-bold mb-1">{t('deviceSettingsModal.notificationsTitle')}</h6>
            <p className="small text-muted mb-0">{t('deviceSettingsModal.notificationsHint')}</p>
          </div>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            style={{ borderRadius: 12 }}
            onClick={thresholdSaveHandler}
            disabled={!canSaveThresholdsEffective || saving || !thresholdSaveHandler}
          >
            {saving ? `${t('save')}...` : t('save')}
          </button>
        </div>
        {showClimateNotificationDelivery ? (
          <div className={`alert alert-${climateDeliveryTone} py-2 px-3 mb-3`}>
            <div className="fw-semibold">{climateDeliveryTitle}</div>
            <div className="small">
              {t(
                'deviceSettingsModal.climateDeliveryCounts',
                notificationDelivery.climateEnabledRecipientCount || 0,
                notificationDelivery.eligibleRecipientCount || 0
              )}
            </div>
            {climateDeliveryWarning ? (
              <div className="small mt-1">{climateDeliveryWarning}</div>
            ) : null}
            {canEnableClimateFromSite ? (
              <button
                type="button"
                className="btn btn-sm btn-outline-dark mt-2"
                onClick={onEnableClimateAlerts}
                disabled={notificationActionPending}
              >
                {notificationActionPending
                  ? `${t('deviceSettingsModal.enablingClimateAlerts')}...`
                  : t('deviceSettingsModal.enableClimateAlertsForMe')}
              </button>
            ) : climateDeliveryWarning ? (
              <div className="small mt-2">
                {t('deviceSettingsModal.climateDeliveryTelegramSettingsHint')}
              </div>
            ) : null}
          </div>
        ) : null}
        <div className="form-check form-switch mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            checked={telegramEnabled}
            onChange={(event) => setTelegramEnabled(event.target.checked)}
            id="deviceTelegramEnabled"
          />
          <label className="form-check-label" htmlFor="deviceTelegramEnabled">
            {t('deviceSettingsModal.telegramAlerts')}
          </label>
        </div>
        <div className="form-check form-switch mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            checked={powerAlerts}
            onChange={(event) => setPowerAlerts(event.target.checked)}
            id="devicePowerAlerts"
          />
          <label className="form-check-label" htmlFor="devicePowerAlerts">
            {t('deviceSettingsModal.powerLoss')}
          </label>
        </div>
        <div className="form-check form-switch mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            checked={thresholdAlerts}
            onChange={(event) => setThresholdAlerts(event.target.checked)}
            id="deviceThresholdAlerts"
          />
          <label className="form-check-label" htmlFor="deviceThresholdAlerts">
            {t('deviceSettingsModal.thresholdAlerts')}
          </label>
        </div>
        <div className="form-check form-switch mb-4">
          <input
            className="form-check-input"
            type="checkbox"
            checked={trendAlerts}
            onChange={(event) => setTrendAlerts(event.target.checked)}
            id="deviceTrendAlerts"
          />
          <label className="form-check-label" htmlFor="deviceTrendAlerts">
            {t('deviceSettingsModal.trendAlerts')}
          </label>
        </div>
      </div>

      {canRestartController ? (
        <div className="device-settings-modal__section">
          <div className="device-settings-modal__section-head">
            <div>
              <h6 className="fw-bold mb-1">{t('deviceSettingsModal.actionsTitle')}</h6>
              <p className="small text-muted mb-0">{t('deviceSettingsModal.actionsHint')}</p>
            </div>
          </div>
          {restartStatus ? <div className="mb-2">{renderStatus(restartStatus)}</div> : null}
          {showRestartConfirm ? (
            <div className="alert alert-warning mb-0" style={{ borderRadius: 12 }}>
              <div className="fw-semibold mb-1">{t('deviceSettingsModal.restartConfirmTitle')}</div>
              <div className="small mb-3">{t('deviceSettingsModal.restartWarning')}</div>
              <div className="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  style={{ borderRadius: 12 }}
                  disabled={restartPending}
                  onClick={async () => {
                    setShowRestartConfirm(false);
                    await onRestartController?.();
                  }}
                >
                  {restartPending ? `${t('deviceSettingsModal.restartSending')}...` : t('deviceSettingsModal.restartConfirmButton')}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  style={{ borderRadius: 12 }}
                  disabled={restartPending}
                  onClick={() => setShowRestartConfirm(false)}
                >
                  {t('cancel')}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              style={{ borderRadius: 12 }}
              disabled={restartPending || !onRestartController}
              onClick={() => setShowRestartConfirm(true)}
            >
              {restartPending ? t('deviceSettingsModal.restartSending') : t('deviceSettingsModal.restartButton')}
            </button>
          )}
        </div>
      ) : null}

      {controller === 'climate' ? (
        <div className="device-settings-modal__section">
          <div className="device-settings-modal__section-head">
            <div>
              <h6 className="fw-bold mb-1">{t('deviceSettingsModal.co2Management')}</h6>
              <p className="small text-muted mb-0">{t('deviceSettingsModal.co2ManagementHint')}</p>
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label">{t('deviceSettingsModal.co2Offset')}</label>
            <input
              type="number"
              className="form-control"
              style={{ maxWidth: 200 }}
              value={co2OffsetDraft}
              onChange={(event) => setCo2OffsetDraft(event.target.value)}
            />
          </div>
          {pendingCalibration ? (
            <div className="alert alert-info py-2 px-3 mb-0" style={{ borderRadius: 12 }}>
              {t('calibrationStatus.pending')}
            </div>
          ) : calibrationStartedUi ? (
            <div className="alert alert-success py-2 px-3 mb-0" style={{ borderRadius: 12 }}>
              {t('calibrationStatus.started', calibrationCountdownText)}
            </div>
          ) : (
            <div className="d-flex gap-2 align-items-center flex-wrap">
              <button
                type="button"
                className="btn btn-sm btn-outline-warning"
                style={{ borderRadius: 12 }}
                onClick={() => setShowCalibrateConfirm(true)}
                disabled={timeSyncPending}
              >
                {t('deviceSettingsModal.startCalibration')}
              </button>
              <span className="small text-muted">{t('deviceSettingsModal.calibrationHint')}</span>
            </div>
          )}
          {showCalibrateConfirm && !pendingCalibration ? (
            <div className="alert alert-warning mt-3 mb-0" style={{ borderRadius: 12 }}>
              <label className="form-label">{t('deviceSettingsModal.calibrationTargetPpmLabel')}</label>
              <input
                type="number"
                className="form-control mb-2"
                style={{ maxWidth: 260 }}
                min="300"
                max="5000"
                value={calibrationTargetPpm ?? ''}
                onChange={(event) => setCalibrationTargetPpm?.(event.target.value)}
                placeholder={t('deviceSettingsModal.calibrationTargetPpmPlaceholder')}
              />
              <div className="mb-2">{t('deviceSettingsModal.calibrationConfirm')}</div>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-warning"
                  style={{ borderRadius: 12 }}
                  onClick={async () => {
                    setShowCalibrateConfirm(false);
                    await onForceCalibrate?.(calibrationTargetPpm);
                  }}
                >
                  {t('yes')}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  style={{ borderRadius: 12 }}
                  onClick={() => setShowCalibrateConfirm(false)}
                >
                  {t('no')}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="device-settings-modal__section">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
          <div>
            <h6 className="fw-bold mb-1">{t('thresholdValues')}</h6>
            <p className="small text-muted mb-0">{t('deviceSettingsModal.thresholdHint')}</p>
          </div>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            style={{ borderRadius: 12 }}
            onClick={thresholdSaveHandler}
            disabled={!canSaveThresholdsEffective || saving || !thresholdSaveHandler}
          >
            {saving ? `${t('save')}...` : t('climateSaveThresholds')}
          </button>
        </div>
        {saving && settingsProgressText ? (
          <div className="small text-primary d-flex align-items-center gap-2 mb-2">
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            <span>{settingsProgressText}</span>
          </div>
        ) : null}
        {settingsStatus ? <div className="mb-2">{renderStatus(settingsStatus)}</div> : null}
        {controller === 'water' ? (
          <>
            {waterRows.map(([key, label]) => (
              <div className="row g-2 align-items-center mb-2" key={key}>
                <div className="col-4">
                  <label className="form-label mb-0">
                    {(() => {
                      const translationKey = `deviceSettingsModal.waterMetric.${key}`;
                      const translated = t(translationKey);
                      return translated && translated !== translationKey ? translated : label;
                    })()}
                  </label>
                </div>
                <ThresholdInputs
                  value={thresholds[key]}
                  onChangeMin={(value) => setThresholdField(key, 'min', value)}
                  onChangeMax={(value) => setThresholdField(key, 'max', value)}
                  t={t}
                />
              </div>
            ))}
            {setWaterConfigField ? (
              <div className="device-settings-modal__threshold-card mt-3">
                <h6 className="fw-bold mb-3">{t('tank') || 'Tank'}</h6>
                {waterConfigRows.map(([key, label, unit]) => (
                  <div className="row g-2 align-items-center mb-2" key={key}>
                    <div className="col-12 col-sm-4">
                      <label className="form-label mb-0">{label}</label>
                    </div>
                    <div className="col-8 col-sm-5">
                      <input
                        type="number"
                        className="form-control"
                        value={waterConfig?.[key] ?? ''}
                        onChange={(event) => setWaterConfigField(key, event.target.value)}
                      />
                    </div>
                    <div className="col-4 col-sm-3">
                      <span className="small text-muted">{unit}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </>
        ) : (
          <div className="row">
            {climateRows.map((section) => (
              <div className="col-12 col-md-6" key={section.section}>
                <div className="device-settings-modal__threshold-card h-100">
                  <h6 className="fw-bold mb-3">{section.title}</h6>
                  {section.items.map(([metric, label]) => (
                    <div className="row g-2 align-items-center mb-2" key={`${section.section}-${metric}`}>
                      <div className="col-12 col-sm-4">
                        <label className="form-label mb-0">{label}</label>
                      </div>
                      <ThresholdInputs
                        value={thresholds[section.section]?.[metric]}
                        onChangeMin={(value) => setThresholdField(section.section, metric, 'min', value)}
                        onChangeMax={(value) => setThresholdField(section.section, metric, 'max', value)}
                        t={t}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {controller === 'climate' ? (
        <div className="device-settings-modal__section">
          <div className="device-settings-modal__section-head">
            <div>
              <h6 className="fw-bold mb-1">{t('deviceSettingsModal.relayManagement')}</h6>
              <p className="small text-muted mb-0">
                {t('deviceSettingsModal.controlModelHint') !== 'deviceSettingsModal.controlModelHint'
                  ? t('deviceSettingsModal.controlModelHint')
                  : 'Automatic scenario control is the default. Manual control is shown as an override state.'}
              </p>
            </div>
          </div>
          {relayCommandStatus ? <div className="mb-2">{renderStatus(relayCommandStatus)}</div> : null}
          <div className="device-settings-modal__relay-grid">
            <RelayModeControl
              relayKey="ac"
              label={t('relayLabels.A')}
              value={relayStatuses.ac}
              mode={relayModes.ac}
              pending={relayPending.ac}
              onRelayCommand={onRelayCommand}
              t={t}
              disabled={relayControlsDisabled}
              stale={relayStale}
            />
            <RelayModeControl
              relayKey="dryer"
              label={t('relayLabels.D')}
              value={relayStatuses.dryer}
              mode={relayModes.dryer}
              pending={relayPending.dryer}
              onRelayCommand={onRelayCommand}
              t={t}
              disabled={relayControlsDisabled}
              stale={relayStale}
            />
            <RelayModeControl
              relayKey="co2"
              label={t('relayLabels.C')}
              value={relayStatuses.co2}
              mode={relayModes.co2}
              pending={relayPending.co2}
              onRelayCommand={onRelayCommand}
              t={t}
              disabled={relayControlsDisabled}
              stale={relayStale}
            />
            <RelayModeControl
              relayKey="extra"
              label={t('relayLabels.reserve')}
              value={relayStatuses.extra}
              mode={relayModes.extra}
              pending={relayPending.extra}
              onRelayCommand={onRelayCommand}
              t={t}
              disabled={relayControlsDisabled}
              stale={relayStale}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
